<template slot="items">
  <tr>
    <!-- Nombre del Producto (se llena al elegir el Reg. Sanitario) -->
    <td>
      <div class="prod-name">
        <span class="prod-name__text">
          {{ row.product ? row.product.name : '—' }}
        </span>
        <span
          v-if="expiryStatus"
          class="prod-exp"
          :class="`prod-exp--${expiryStatus.type}`"
          :title="expiryStatus.title"
        >
          <v-icon x-small class="prod-exp__icon">schedule</v-icon>
          {{ expiryStatus.label }}
        </span>
      </div>
    </td>

    <!-- Presentación Comercial -->
    <td>{{ row.presentation || '—' }}</td>

    <!-- Reg. Sanitario (buscador principal) -->
    <td>
      <v-autocomplete
        no-data-text="Datos no disponibles"
        placeholder="Buscar por Reg. Sanitario"
        :items="regItems"
        :disabled="disabled"
        item-text="sanitaryReg"
        item-value="sanitaryReg"
        clearable
        hide-details
        dense
        v-model="selReg"
        @change="onSelectReg"
      ></v-autocomplete>
    </td>

    <!-- Laboratorio -->
    <td>{{ row.laboratory || labName || '—' }}</td>

    <!-- Órgano Anuente: sale solo del producto. Aquí se busca únicamente si
         el producto no lo trae o hay que corregirlo, y queda guardado en él. -->
    <td :title="organoName">
      <v-autocomplete
        no-data-text="Ningún órgano coincide"
        :placeholder="
          row.product ? 'Buscar por siglas o nombre' : 'Elija el Reg. Sanitario'
        "
        :items="organoItems"
        :filter="filterOrgano"
        :disabled="disabled || !row.product"
        :menu-props="{ maxHeight: 340, minWidth: 380 }"
        item-text="label"
        item-value="id"
        auto-select-first
        hide-details
        dense
        v-model="row.organoAnuenteId"
        @change="onOrganoChange"
      >
        <template slot="item" slot-scope="{ item }">
          <span class="org-opt">
            <span v-if="item.sigla" class="org-opt__sigla">
              {{ item.sigla }}
            </span>
            <span class="org-opt__name">{{ item.base }}</span>
          </span>
        </template>
      </v-autocomplete>
    </td>

    <!-- Acciones -->
    <td class="text-right">
      <div class="prod-actions">
        <v-btn
          class="prod-btn prod-btn--edit"
          icon
          small
          :disabled="disabled"
          title="Cambiar registro"
          @click="resetSelection"
        >
          <v-icon small>edit</v-icon>
        </v-btn>
        <v-btn
          class="prod-btn prod-btn--del"
          icon
          small
          :disabled="disabled"
          title="Quitar producto"
          @click="removeRow"
        >
          <v-icon small>delete</v-icon>
        </v-btn>
      </div>
    </td>
  </tr>
</template>

<script>
import moment from 'moment-timezone'
import { dateFormat } from '@/helpers/TransitHelper'
import { mapGetters } from 'vuex'

// Minúsculas y sin tildes, para que "panama" encuentre "Panamá".
const normalize = text =>
  String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()

// "Ministerio de Salud (MINSA)" -> sigla "MINSA", base "Ministerio de Salud".
const toOrganoOption = organo => {
  const match = /^(.*?)\s*\(([^()]+)\)\s*$/.exec(organo.name || '')
  const sigla = match ? match[2].trim() : ''
  const base = match ? match[1].trim() : organo.name
  const label = sigla ? `${sigla} — ${base}` : base
  return { id: organo.id, name: organo.name, sigla, base, label }
}

export default {
  props: {
    row: {
      type: Object,
      required: true,
    },
    // Catálogo de registros sanitarios del laboratorio seleccionado.
    regCatalog: {
      type: Array,
      default: () => [],
    },
    organosAnuentes: {
      type: Array,
      default: () => [],
    },
    labName: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    index: Number,
  },
  data() {
    return {
      selReg: this.row.sanitaryReg || null,
    }
  },
  computed: {
    ...mapGetters({
      InvoiceProducts: 'invoices/emptyItems',
    }),
    // Opciones del órgano anuente ordenadas por siglas. Arriba van los que
    // ya usan los productos de este laboratorio (el más frecuente primero),
    // que casi siempre es el que corresponde.
    organoItems() {
      const options = this.organosAnuentes
        .map(toOrganoOption)
        .sort((a, b) => a.label.localeCompare(b.label, 'es'))

      const productsByOrgano = {}
      const seen = new Set()
      this.regCatalog.forEach(entry => {
        if (!entry.organoAnuenteId || seen.has(entry.productId)) return
        seen.add(entry.productId)
        productsByOrgano[entry.organoAnuenteId] =
          (productsByOrgano[entry.organoAnuenteId] || 0) + 1
      })
      const used = options
        .filter(o => productsByOrgano[o.id])
        .sort((a, b) => productsByOrgano[b.id] - productsByOrgano[a.id])
      if (!used.length) return options

      const rest = options.filter(o => !productsByOrgano[o.id])
      const lab = this.row.laboratory || this.labName
      return [
        { header: lab ? `Usados en ${lab}` : 'Usados en este laboratorio' },
        ...used,
        { divider: true },
        { header: 'Todos' },
        ...rest,
      ]
    },
    // Nombre completo para el tooltip: en la columna solo caben las siglas.
    organoName() {
      const organo = this.organosAnuentes.find(
        o => o.id === this.row.organoAnuenteId
      )
      return organo ? organo.name : ''
    },
    // Incluye el registro que ya trae la fila (modo edición) aunque el
    // catálogo del laboratorio aún no lo tenga, para que se vea seleccionado.
    regItems() {
      const base = this.regCatalog || []
      if (
        this.row.sanitaryReg &&
        !base.some(r => r.sanitaryReg === this.row.sanitaryReg)
      ) {
        return [this.rowAsEntry(), ...base]
      }
      return base
    },
    // Indica si el registro está vencido o próximo a vencer (6 meses).
    expiryStatus() {
      if (!this.row.expirationDate) return null
      const exp = moment(this.row.expirationDate)
      const today = moment()
      if (exp.isBefore(today)) {
        return {
          type: 'expired',
          label: 'Vencido',
          title: `Venció el ${dateFormat(this.row.expirationDate)}`,
        }
      }
      if (exp.isBefore(moment().add(6, 'months'))) {
        return {
          type: 'soon',
          label: 'Próximo a vencer',
          title: `Vence el ${dateFormat(this.row.expirationDate)}`,
        }
      }
      return null
    },
  },
  methods: {
    dateFormat,
    rowAsEntry() {
      return {
        sanitaryReg: this.row.sanitaryReg,
        productName: this.row.product ? this.row.product.name : '',
        productId: this.row.product ? this.row.product.id : null,
        presentation: this.row.presentation,
        laboratory: this.row.laboratory,
        organoAnuenteId: this.row.organoAnuenteId,
        expirationDate: this.row.expirationDate,
        batchId: this.row.batchId,
      }
    },
    onSelectReg(reg) {
      if (!reg) {
        this.$set(this.row, 'product', null)
        this.$set(this.row, 'presentation', '')
        this.$set(this.row, 'sanitaryReg', '')
        this.$set(this.row, 'batchId', null)
        this.$set(this.row, 'expirationDate', null)
        this.$set(this.row, 'organoAnuenteId', null)
        return
      }
      const entry = this.regItems.find(r => r.sanitaryReg === reg)
      if (!entry) return
      this.$set(this.row, 'product', {
        name: entry.productName,
        id: entry.productId,
      })
      this.$set(this.row, 'presentation', entry.presentation)
      this.$set(this.row, 'sanitaryReg', entry.sanitaryReg)
      this.$set(this.row, 'laboratory', entry.laboratory || this.labName)
      this.$set(this.row, 'batchId', entry.batchId)
      this.$set(this.row, 'expirationDate', entry.expirationDate)
      // El órgano es el del producto; si no trae, se deja vacío para que no
      // quede el del producto que estaba antes en la fila.
      this.$set(this.row, 'organoAnuenteId', entry.organoAnuenteId || null)
    },
    // Busca por siglas o por nombre, sin importar tildes ni el orden de las
    // palabras ("salud ministerio" encuentra MINSA).
    filterOrgano(item, query) {
      if (item.header || item.divider) return false
      const text = normalize(`${item.sigla} ${item.name}`)
      return normalize(query)
        .split(/\s+/)
        .every(word => text.includes(word))
    },
    onOrganoChange(id) {
      // El órgano queda guardado en el producto apenas se elige.
      this.$emit('set-organo', {
        productId: this.row.product ? this.row.product.id : null,
        organoAnuenteId: id,
      })
    },
    resetSelection() {
      this.selReg = null
      this.onSelectReg(null)
    },
    removeRow() {
      this.InvoiceProducts.splice(this.index, 1)
    },
  },
}
</script>

<style scoped>
.prod-name {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.prod-name__text {
  font-weight: 600;
  color: #0f172a;
}

.prod-exp {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.prod-exp__icon {
  font-size: 13px !important;
}

.prod-exp--soon {
  background: rgba(234, 88, 12, 0.12);
  color: #c2410c;
}

.prod-exp--soon .prod-exp__icon {
  color: #ea580c !important;
}

.prod-exp--expired {
  background: rgba(229, 37, 42, 0.12);
  color: #b91c1c;
}

.prod-exp--expired .prod-exp__icon {
  color: #dc2626 !important;
}

.text-right {
  text-align: right;
}

.prod-actions {
  display: inline-flex;
  gap: 6px;
}

.prod-btn--edit {
  color: #254f87 !important;
}

.prod-btn--del {
  color: #e5252a !important;
}

/* Opción del órgano anuente: siglas en una columna fija y el nombre al lado,
   para encontrarlo de un vistazo. */
.org-opt {
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
  font-size: 13px;
}

.org-opt__sigla {
  flex: 0 0 76px;
  font-weight: 700;
  color: #254f87;
}

.org-opt__name {
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
